import { formatDate, formatDateRange } from '@unimpaired/utils'
import { twMerge } from 'tailwind-merge'
import {
  getBgColorClasses,
  getBorderColorClasses,
  getTimeColorClasses,
  getTitleColorClasses,
} from './schedule.colors'
import {
  getColumnEventIsIn,
  getGridRow,
  totalDayScheduleColumns,
} from './schedule.positioning.helpers'
import { DayScheduleProps, ScheduleEvent } from './DaySchedule.types'
import { RenderHTMLContent } from '../Text'

export type DayScheduleEventProps<T extends ScheduleEvent> = {
  startDayAt: Date
  endDayAt: Date
  colStart?: number
  colWidth?: number
  className?: string
  layoutId: string
  onClick: () => any
  minDisplayDurationMinutes?: number
  condensed?: boolean
  occluded?: boolean
  timezone?: string
  hideTimezone?: boolean
  minEventWidth: number
} & ScheduleEvent

export const DayScheduleEvent = <T extends ScheduleEvent>({
  startDayAt,
  endDayAt,
  colStart,
  colWidth,
  className,
  onClick,
  layoutId,
  eventDescriptor,
  minDisplayDurationMinutes = 15,
  timezone,
  hideTimezone,
  minEventWidth,
  ...event
}: DayScheduleEventProps<T>) => {
  // Layout invariant: the grid has 12 rows per hour (5-min buckets) and 60
  // columns total. `colStart`/`colWidth` come from positionEvents; events
  // without a column assignment span the full width as a safe fallback.
  const gridRow = getGridRow({
    eventStartTime: event.startTime,
    eventEndTime: event.endTime,
    startDayAt,
    endDayAt,
    minDisplayDurationMinutes,
  })
  const gridColumn = `${colStart || 1} / span ${colWidth ?? totalDayScheduleColumns}`

  const eventColor = event.color ?? 'gray'
  return (
    <li
      className={twMerge(
        'relative mt-px flex overflow-visible',
        event.muted && 'opacity-50',
        className,
      )}
      style={{ gridRow, gridColumn, minWidth: minEventWidth + 'px' }}>
      <button
        type='button'
        onClick={(e) => {
          e.stopPropagation()
          if (onClick) onClick()
        }}
        className={twMerge(
          `group transition-all mb-1 mx-1 gap-x-4 border-1 border-l-4 shadow overflow-hidden flex flex-col min-w-0 flex-1 w-0 justify-start items-start content-start grow rounded-lg px-2 hover:px-2.5 hover:pb-0.5 text-xs leading-5`,
          !onClick && 'cursor-pointer',
          getBgColorClasses(eventColor as any),
          getBorderColorClasses(eventColor as any),
        )}>
        <div
          className={twMerge(
            'flex flex-wrap gap-x-4 justify-start items-start content-start max-w-full',
            event.condensed && 'flex-nowrap',
          )}>
          <span
            className={twMerge(
              'whitespace-nowrap min-w-fit overflow-hidden',
              getTimeColorClasses(eventColor as any),
            )}>
            {event.condensed
              ? formatDate('time', event.startTime)
              : formatDateRange(
                  ['time'],
                  [event.startTime, event.endTime],
                  timezone ?? event.venue?.timezone,
                  hideTimezone,
                )}
          </span>
          <span
            className={twMerge(
              'font-semibold min-w-min shrink overflow-hidden text-ellipsis text-left',
              event.condensed && 'flex-1 min-w-0 shrink grow text-nowrap',
              getTitleColorClasses(eventColor as any),
            )}>
            {event.name}
          </span>
        </div>
        {event.headline && (
          <RenderHTMLContent
            className={twMerge(
              'overflow-hidden text-wrap pt-0.5 text-xs overflow-ellipsis text-left min-w-min max-w-full flex-1 self-stretch pb-1 text-base-1000',
            )}>
            {event.headline}
          </RenderHTMLContent>
        )}
      </button>
    </li>
  )
}

export const getDayScheduleLayoutId = <T extends ScheduleEvent>(
  columnateBy: DayScheduleProps<T>['columnateBy'],
  event: T,
  ...otherKeys: (string | number)[]
) =>
  event.name +
  (getColumnEventIsIn(columnateBy)(event)?.key || '') +
  otherKeys.join('-')
